import { useCallback, useEffect, useRef, useState } from 'react';
import { DonationHeader } from './DonationHeader';
import { useLocale } from '@/context/LocaleContext';
import '@/styles/donation-layout.css';
import {
  useCreateDonationPayment,
  useDonationPackages,
  useDonationHistory,
  type DonationMethod,
  type DonationPackage,
} from '@/hooks/useDonation';

declare global {
  interface Window {
    Stripe?: new (key: string) => StripeInstance;
  }
}

interface StripeInstance {
  elements(): { create(type: string, options?: Record<string, unknown>): StripeElement };
  confirmCardPayment(
    clientSecret: string,
    data: { payment_method: { card: StripeElement } },
  ): Promise<{ error?: { message?: string } }>;
}

interface StripeElement {
  mount(selector: string): void;
  on(event: string, handler: (ev: { error?: { message?: string } }) => void): void;
}

function loadStripeScript(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Stripe SDK failed'));
    document.head.appendChild(script);
  });
}

function formatPackageNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

function DonationSummaryBox({
  packageName,
  silks,
  amount,
}: {
  packageName: string;
  silks: number;
  amount: number;
}) {
  const { t } = useLocale();
  return (
    <div id="donation-summary" className="donation-summary-box">
      <div className="donation-summary-item">
        <span className="donation-summary-label">{t('don_package')}</span>
        <span className="donation-summary-value" id="summary-package-name">
          {packageName || '-'}
        </span>
      </div>
      <div className="donation-summary-item">
        <span className="donation-summary-label">{t('don_silks')}</span>
        <span className="donation-summary-value" id="summary-silks">
          {silks.toLocaleString()} {t('don_silks_label')}
        </span>
      </div>
      <div className="donation-summary-item">
        <span className="donation-summary-label">{t('don_price')}</span>
        <span className="donation-summary-value" id="summary-price">
          ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
    </div>
  );
}

export function DonationPage() {
  const { t, locale } = useLocale();
  const { data } = useDonationPackages();
  const createPayment = useCreateDonationPayment();
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const history = useDonationHistory(historyPage, showHistory);

  const packages = (data?.packages ?? []).slice(0, 6);
  const methods = data?.methods ?? [];

  const [showPart1, setShowPart1] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [selectedPackageName, setSelectedPackageName] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [selectedSilks, setSelectedSilks] = useState(0);
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [visiblePaymentMethod, setVisiblePaymentMethod] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paypalUrl, setPaypalUrl] = useState<string | null>(null);
  const [stripeTotal, setStripeTotal] = useState('0.00');

  const currentDonationIdRef = useRef<number | null>(null);
  const stripeRef = useRef<StripeInstance | null>(null);
  const stripePublishableKeyRef = useRef<string | null>(null);
  const stripeCardElementRef = useRef<StripeElement | null>(null);
  const stripeClientSecretRef = useRef<string | null>(null);
  const autoStripePendingRef = useRef(false);

  const hideAllPaymentMethods = useCallback(() => {
    setVisiblePaymentMethod(null);
    setPaypalUrl(null);
  }, []);

  const loadPayPal = useCallback((approvalUrl: string) => {
    setVisiblePaymentMethod('paypal');
    setPaypalUrl(approvalUrl);
  }, []);

  const initializeStripePayment = useCallback((clientSecret: string) => {
    if (!window.Stripe || !stripePublishableKeyRef.current) {
      window.alert(t('don_stripe_not_loaded'));
      return;
    }

    stripeRef.current = new window.Stripe(stripePublishableKeyRef.current);
    const elements = stripeRef.current.elements();

    stripeCardElementRef.current = elements.create('card', {
      style: {
        base: {
          color: '#e6c98a',
          fontFamily: 'Arial, sans-serif',
          fontSize: '16px',
          '::placeholder': { color: '#a0a0a0' },
        },
      },
    });

    const mountTarget = document.getElementById('stripe-card-element');
    if (mountTarget) {
      mountTarget.innerHTML = '';
      stripeCardElementRef.current.mount('#stripe-card-element');
      stripeCardElementRef.current.on('change', (event) => {
        const displayError = document.getElementById('stripe-card-errors');
        if (displayError) {
          displayError.textContent = event.error?.message ?? '';
        }
      });
    }

    stripeClientSecretRef.current = clientSecret;

    const form = document.getElementById('donation-stripe-form');
    if (!form) return;

    const handler = (event: Event) => {
      event.preventDefault();
      if (!stripeRef.current || !stripeCardElementRef.current || !stripeClientSecretRef.current) return;

      void stripeRef.current
        .confirmCardPayment(stripeClientSecretRef.current, {
          payment_method: { card: stripeCardElementRef.current },
        })
        .then((result) => {
          if (result.error) {
            const displayError = document.getElementById('stripe-card-errors');
            if (displayError) displayError.textContent = result.error.message ?? '';
          } else if (currentDonationIdRef.current) {
            window.location.href = `/donation/success?donation_id=${currentDonationIdRef.current}`;
          }
        });
    };

    form.removeEventListener('submit', handler);
    form.addEventListener('submit', handler);
  }, [t]);

  const loadStripe = useCallback(
    async (clientSecret: string, publishableKey: string) => {
      setVisiblePaymentMethod('stripe');
      stripePublishableKeyRef.current = publishableKey;

      if (typeof window.Stripe === 'undefined') {
        await loadStripeScript();
      }
      initializeStripePayment(clientSecret);
    },
    [initializeStripePayment],
  );

  const createPaymentFlow = useCallback(
    async (methodId: number, _methodKey: string) => {
      if (selectedAmount <= 0 || !methodId) return;

      setLoading(true);
      hideAllPaymentMethods();
      setPaymentError('');

      try {
        const result = await createPayment.mutateAsync({
          amount: selectedAmount,
          method_id: methodId,
          silks_amount: selectedSilks,
        });

        currentDonationIdRef.current = result.donation_id;

        if (result.method === 'paypal' && result.approval_url) {
          loadPayPal(result.approval_url);
        } else if (result.method === 'stripe' && result.client_secret && result.publishable_key) {
          setStripeTotal(selectedAmount.toFixed(2));
          await loadStripe(result.client_secret, result.publishable_key);
        } else if (result.method === 'binance') {
          setVisiblePaymentMethod('binance');
        }
      } catch (err) {
        const response = (err as { response?: { data?: { code?: string; message?: string; data?: Record<string, number> } } })?.response?.data;
        const msg = response?.code === 'DAILY_LIMIT' && response.data
          ? t('don_daily_limit', Object.fromEntries(Object.entries(response.data).map(([key, value]) =>
              [key, new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(value)])))
          : response?.code === 'DONATION_COOLDOWN' ? t('don_limit_locked') : response?.message ?? t('don_error_create');
        setPaymentError(msg);
      } finally {
        setLoading(false);
      }
    },
    [createPayment, hideAllPaymentMethods, loadPayPal, loadStripe, selectedAmount, selectedSilks, t, locale],
  );

  const selectPaymentMethod = useCallback(
    (method: DonationMethod) => {
      if (
        selectedMethodId === method.id &&
        visiblePaymentMethod === method.method_key &&
        !paymentError
      ) {
        setVisiblePaymentMethod(method.method_key);
        return;
      }
      setSelectedMethodId(method.id);
      hideAllPaymentMethods();
      void createPaymentFlow(method.id, method.method_key);
    },
    [createPaymentFlow, hideAllPaymentMethods, paymentError, selectedMethodId, visiblePaymentMethod],
  );

  const showPaymentMethods = useCallback(() => {
    setShowPart1(false);
    setShowSummary(true);
    setStripeTotal(selectedAmount.toFixed(2));
    autoStripePendingRef.current = true;
  }, [selectedAmount]);

  const selectPackage = useCallback(
    (box: DonationPackage) => {
      setSelectedPackageId(box.id);
      setSelectedPackageName(box.name);
      setSelectedAmount(Number(box.amount));
      setSelectedSilks(Number(box.silks_amount));
      showPaymentMethods();
    },
    [showPaymentMethods],
  );

  const showSilkSelection = useCallback(() => {
    setShowPart1(true);
    setShowSummary(false);
    hideAllPaymentMethods();
    setSelectedMethodId(null);
    setPaymentError('');
  }, [hideAllPaymentMethods]);

  useEffect(() => {
    if (!autoStripePendingRef.current || !showSummary) return;
    autoStripePendingRef.current = false;

    const stripeMethod = methods.find((m) => m.method_key === 'stripe');
    if (!stripeMethod) return;

    const timer = window.setTimeout(() => {
      selectPaymentMethod(stripeMethod);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [methods, selectPaymentMethod, showSummary]);

  return (
    <div className="container-ingame donation-page">
      <div className="donation-container">
        <DonationHeader />

        <div className="donation-body">
          {showPart1 && !showHistory ? <nav className="donation-section-nav" aria-label={t('donation')}>
            <button type="button" className="donation-btn" aria-pressed={!showHistory} onClick={() => setShowHistory(false)}>
              <img src="/assets/icons/donation.svg" alt="" className="donation-page-icon" />{t('donation')}
            </button>
            <button type="button" className="donation-btn" aria-pressed={showHistory} onClick={() => setShowHistory(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M5 3h14v18l-3-2-4 2-4-2-3 2V3Z M8 8h8 M8 12h8 M8 16h4" /></svg>
              {t('don_history')}
            </button>
          </nav> : null}
          {showHistory ? <section className="donation-history" aria-label={t('don_history')}>
            <button type="button" className="donation-back-btn" onClick={() => setShowHistory(false)}>{t('don_back')}</button>
            <h2>{t('don_history')}</h2>
            {history.isLoading ? <p role="status">{t('don_history_loading')}</p> : history.isError ? <div role="alert"><p>{t('don_history_error')}</p><button className="donation-btn" onClick={() => void history.refetch()}>{t('reg_retry')}</button></div> : <>
              {!history.data?.items.length ? <p>{t('don_history_empty')}</p> : <div className="donation-history-scroll"><table>
                <thead><tr><th>ID</th><th>{t('don_history_date')}</th><th>{t('don_history_method')}</th><th>{t('don_price')}</th><th>{t('don_silks')}</th><th>{t('don_history_status')}</th></tr></thead>
                <tbody>{history.data.items.map(item => <tr key={item.id}>
                  <td>#{item.id}</td><td>{new Date(item.created_at).toLocaleString(locale)}</td><td>{item.method_name ?? '—'}</td>
                  <td>{new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(Number(item.amount))}</td><td>{item.silks_amount.toLocaleString(locale)}</td>
                  <td><span className={`donation-history-status donation-history-status--${item.status}`}>{t(`don_status_${item.status}`)}</span></td>
                </tr>)}</tbody>
              </table></div>}
              <div className="donation-history-pages">
                <button className="donation-btn" disabled={historyPage === 1} onClick={() => setHistoryPage(page => page - 1)}>{t('don_history_previous')}</button>
                <span>{historyPage}</span>
                <button className="donation-btn" disabled={!history.data?.hasMore} onClick={() => setHistoryPage(page => page + 1)}>{t('don_history_next')}</button>
              </div>
            </>}
          </section> : null}
          <div id="donation-part1" className="donation-part" style={{ display: !showHistory && showPart1 ? 'block' : 'none' }}>
            <div className="donation-packages-grid">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`donation-package-box${selectedPackageId === pkg.id ? ' selected' : ''}`}
                  data-package-id={pkg.id}
                  data-package-name={pkg.name}
                  data-amount={pkg.amount}
                  data-silks={pkg.silks_amount}
                  data-vip-points={pkg.vip_points}
                  data-promotion-id={pkg.promotion_id ?? ''}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {if (event.key === 'Enter' || event.key === ' ') {event.preventDefault(); selectPackage(pkg);}}}
                  onClick={() => selectPackage(pkg)}
                >
                  {pkg.promotion_id ? <div className="donation-package-badge">{t('don_promo')}</div> : null}
                  <div className="donation-package-name">{pkg.name}</div>
                  <div className="donation-package-silks-amount">
                    {formatPackageNumber(Number(pkg.silks_amount))}
                    <span className="donation-package-silks-label-small">{t('don_silks_label')}</span>
                  </div>
                  <div className="donation-package-price">
                    <span>${formatPackageNumber(Number(pkg.amount))}</span>
                  </div>
                  <div className="donation-package-vip-small">
                    {t('don_vip_points', { amount: formatPackageNumber(Number(pkg.vip_points)) })}
                  </div>
                  {pkg.promotion_id ? (
                    <div className="donation-package-promo-label">{t('don_special_offer')}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div id="donation-part2" className="donation-part" style={{ display: showHistory || showPart1 ? 'none' : 'block' }}>
            <div className="donation-payment-topbar">
              <button
                type="button"
                id="donation-back-btn"
                className="donation-back-btn"
                onClick={showSilkSelection}
              >
                {t('don_back')}
              </button>

              <div className="donation-methods-tabs donation-methods-tabs--inline">
                {methods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    className={`donation-method-tab${selectedMethodId === method.id ? ' active' : ''}`}
                    data-method-id={method.id}
                    data-method-key={method.method_key}
                    onClick={() => selectPaymentMethod(method)}
                  >
                    {method.method_key === 'paypal' ? <i className="fab fa-paypal" /> : null}
                    {method.method_key === 'stripe' ? <i className="fab fa-stripe" /> : null}
                    {method.method_key === 'binance' ? <i className="fab fa-bitcoin" /> : null}
                    <span>{method.method_name}</span>
                  </button>
                ))}
              </div>
            </div>

            {paymentError ? <div className="donation-error">{paymentError}</div> : null}

            <div id="donation-payment-container">
              <div
                id="donation-stripe-container"
                className="donation-payment-method"
                style={{ display: visiblePaymentMethod === 'stripe' ? 'block' : 'none' }}
              >
                <form id="donation-stripe-form">
                  <div
                    id="stripe-card-element"
                    style={{
                      padding: 15,
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: 0,
                      marginBottom: 15,
                    }}
                  />
                  <div
                    id="stripe-card-errors"
                    role="alert"
                    style={{ color: '#ff4444', marginBottom: 15 }}
                  />
                </form>
              </div>

              <div
                id="donation-binance-container"
                className="donation-payment-method"
                style={{ display: visiblePaymentMethod === 'binance' ? 'block' : 'none' }}
              >
                <div style={{ textAlign: 'center', padding: 40, color: '#e6c98a' }}>
                  <p>{t('don_binance_soon')}</p>
                </div>
              </div>
            </div>

            {showSummary ? (
              <div className="donation-checkout-footer">
                <DonationSummaryBox
                  packageName={selectedPackageName}
                  silks={selectedSilks}
                  amount={selectedAmount}
                />

                {visiblePaymentMethod === 'stripe' ? (
                  <button
                    type="submit"
                    form="donation-stripe-form"
                    id="donation-stripe-submit"
                    className="donation-btn donation-btn-primary"
                    style={{ width: '100%' }}
                  >
                    {t('don_pay')}
                    <span id="stripe-total-amount">{stripeTotal}</span>
                  </button>
                ) : null}

                {visiblePaymentMethod === 'paypal' && paypalUrl ? (
                  <div id="paypal-button-container" style={{ textAlign: 'center' }}>
                    <a
                      href={paypalUrl}
                      className="donation-btn donation-btn-primary"
                      style={{ textDecoration: 'none', display: 'inline-block' }}
                    >
                      {t('don_continue_paypal')}
                    </a>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div
            id="donation-loading"
            style={{
              display: loading ? 'flex' : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.8)',
              zIndex: 9999,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div style={{ color: '#e6c98a', fontSize: '1.2em' }}>{t('don_processing')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
